import React, { createContext, useContext, useState, useEffect, ComponentType } from 'react';
import { Platform } from 'react-native';

import colors from './colors';
import { isWeb, setSecureValue, getSecureValue } from '@helpers';
import { ColorFunction, WithColorProps } from './types';
import { clearStatusBarCache } from '@components/common/useStatusBar';
import { useStableColorScheme } from '@v2/pages/common/hooks/useStableColorScheme';

const convertedColors: any = {};

colors.colors.forEach((item) => {
  convertedColors[item.name.replace(/ /g, '')] = item.values.rgba;
});

export const colorsList = convertedColors;

export const checkColor = (c: any, add: string) => {
  const name = `${c} ${add}`.replace(/ /g, '');
  const color = colorsList[name];

  if (color) {
    return name;
  }

  let system;
  if (isWeb) {
    system = '[web]';
  } else {
    system = '[mobile]';
  }
  const systemColorName = `${c}${system}${add}`.replace(/ /g, '');
  const systemColor = colorsList[systemColorName];

  if (systemColor) {
    return systemColorName;
  }

  return c;
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const scheme = useStableColorScheme();

  const [custom, setCustom] = useState(() => getSecureValue('scheme'));

  // Clear StatusBar cache when system theme changes
  useEffect(() => {
    clearStatusBarCache();
  }, [scheme]);

  const updateCustom = async (value: any) => {
    setSecureValue('scheme', value);
    setCustom(value);
    // Clear StatusBar cache so it updates with new theme colors
    clearStatusBarCache();
  };

  const currentScheme = custom !== 'system' && custom ? custom : scheme === null || Platform.isTV ? 'dark' : scheme;

  let currentSchemeName = 'Light';
  if (currentScheme === 'dark') {
    currentSchemeName = 'Dark';
  }

  const Colors = (name: string, includeScheme = true) => {
    let newName = '';
    if (!name) {
      return 'transparent';
    }
    newName = name.replace(/ /g, '');

    const returnSystem = () => {
      let system;
      if (isWeb) {
        system = '[web]';
      } else {
        system = '[mobile]';
      }
      return convertedColors[`${newName}${system}`];
    };

    if (!includeScheme) {
      const color = convertedColors[newName];
      if (color) {
        return color;
      }
      return returnSystem();
    }

    if (name.includes('Light')) {
      newName = newName.replace('/Light/', `/${currentSchemeName}/`);
    } else {
      newName = newName.replace('/Dark/', `/${currentSchemeName}/`);
    }

    const color = convertedColors[newName];
    if (color) {
      return color;
    }

    return returnSystem();
  };
  Colors.scheme = currentScheme;
  Colors.schemeName = currentSchemeName;

  return <ThemeContext.Provider value={{ C: Colors, scheme, custom, updateCustom }}>{children}</ThemeContext.Provider>;
};

export const useColors: any = () => useContext(ThemeContext);

export default <P extends object>(OriginalComponent: ComponentType<P & WithColorProps>) => {
  return (props: Omit<P, keyof WithColorProps>) => {
    const { C, custom, scheme, updateCustom } = useColors();

    return (
      <OriginalComponent
        {...{
          ...(props as P),
          C,
          custom,
          ColorsData: { updateCustom, custom, scheme },
        }}
      />
    );
  };
};

export interface WithColor {
  C: ColorFunction;
}
